import { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Link,
  ListItemIcon,
  CircularProgress,
  Menu,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Resident,
  ResidentDetailed,
  useBlockUserMutation,
  useGetUserByIdQuery,
  useGetResidentPeriodsQuery,
  useGetResidentsQuery,
  useUnblockUserMutation,
  useUpdateResidentCaliberMutation,
  useLazyExportResidentsToXlsxQuery,
} from "../../../entities/admin-communities";
import { useGetCitiesQuery } from "../../../entities/admin-city";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PersonIcon from "@mui/icons-material/Person";
import { ResidentPeriodsView } from "./resident-periods-view";
import SearchIcon from "@mui/icons-material/Search";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import BarChartIcon from "@mui/icons-material/BarChart";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TelegramIcon from "@mui/icons-material/Telegram";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExtensionIcon from "@mui/icons-material/Extension";
import StarIcon from "@mui/icons-material/Star";
import EventIcon from "@mui/icons-material/Event";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import { renderCaliberName } from "../../../shared/lib/caliber";
import { ResidentCaliber } from "../../../entities/admin-events";

const ResidentsManagement = () => {
  // States
  const [search, setSearch] = useState("");
  const [selectedCities, setSelectedCities] = useState<number[]>([]);
  const [selectedCalibers, setSelectedCalibers] = useState<ResidentCaliber[]>(
    [],
  );
  const [blockedFilter, setBlockedFilter] = useState<boolean | undefined>(
    undefined,
  );
  const [sort, setSort] = useState<
    "firstname" | "lastname" | "inviteat" | "createdat"
  >("lastname");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [limit] = useState(25);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedResidentId, setSelectedResidentId] = useState<string | null>(
    null,
  );
  const [openCaliberDialog, setOpenCaliberDialog] = useState(false);
  const [selectedCaliber, setSelectedCaliber] = useState<ResidentCaliber>(
    ResidentCaliber.VOSHOD,
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuResidentId, setMenuResidentId] = useState<string | null>(null);
  const [detailTabValue, setDetailTabValue] = useState(0);
  const [isSorting, setIsSorting] = useState(false);

  // RTK Queries
  const { data: citiesData } = useGetCitiesQuery();
  const {
    data: residentsData,
    refetch: refetchResidents,
    isLoading: isLoadingResidents,
  } = useGetResidentsQuery({
    search,
    cities: selectedCities,
    calibers: selectedCalibers,
    blocked: blockedFilter,
    sort,
    order,
    pageToken,
    limit,
  });
  const { data: residentDetail, isFetching: isFetchingResidentDetail } =
    useGetUserByIdQuery(selectedResidentId || "", {
      skip: !selectedResidentId,
    });

  const { data: residentPeriods, isFetching: isFetchingPeriods } =
    useGetResidentPeriodsQuery(selectedResidentId || "", {
      skip: !selectedResidentId || !openDetailDialog,
    });
  const [updateResidentCaliber] = useUpdateResidentCaliberMutation();
  const [blockUser] = useBlockUserMutation();
  const [unblockUser] = useUnblockUserMutation();
  // Export users to Excel
  const [exportResidentsToXlsx, { isLoading: isExporting }] =
    useLazyExportResidentsToXlsxQuery();

  // Handle export to Excel
  const handleExportToXlsx = async () => {
    try {
      const result = await exportResidentsToXlsx().unwrap();

      // Create download link
      const url = window.URL.createObjectURL(result);
      const link = document.createElement("a");
      link.href = url;
      link.download = `community-residents-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting users:", error);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setPageToken(undefined);
  }, [search, selectedCities, selectedCalibers, blockedFilter, sort, order]);

  // Refetch data when sort or order changes
  useEffect(() => {
    setIsSorting(true);
    refetchResidents().finally(() => {
      setIsSorting(false);
    });
  }, [sort, order, refetchResidents]);

  // Handle opening resident details
  const handleOpenDetails = (residentId: string) => {
    setSelectedResidentId(residentId);
    setOpenDetailDialog(true);
    setDetailTabValue(0); // Reset to first tab
  };

  // Handle closing resident details
  const handleCloseDetails = () => {
    setOpenDetailDialog(false);
    setSelectedResidentId(null);
  };

  // Handle opening caliber dialog
  const handleOpenCaliberDialog = (resident: Resident) => {
    setSelectedResidentId(resident.id);
    setSelectedCaliber(resident.caliber);
    setOpenCaliberDialog(true);
  };

  // Handle closing caliber dialog
  const handleCloseCaliberDialog = () => {
    setOpenCaliberDialog(false);
  };

  // Handle updating resident caliber
  const handleUpdateCaliber = async () => {
    if (selectedResidentId) {
      await updateResidentCaliber({
        id: selectedResidentId,
        caliber: selectedCaliber,
      });
      refetchResidents();
      handleCloseCaliberDialog();
    }
  };

  // Handle block user
  const handleBlockUser = async (userId: string) => {
    await blockUser(userId);
    refetchResidents();
  };

  // Handle unblock user
  const handleUnblockUser = async (userId: string) => {
    await unblockUser(userId);
    refetchResidents();
  };

  // Handle open menu
  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    residentId: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuResidentId(residentId);
  };

  // Handle close menu
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuResidentId(null);
  };

  // Handle menu action
  const handleMenuAction = (action: string) => {
    if (!menuResidentId) return;

    switch (action) {
      case "caliber": {
        const resident = residentsData?.residents.find(
          (r) => r.id === menuResidentId,
        );
        if (resident) {
          handleOpenCaliberDialog(resident);
        }
        break;
      }
      case "block":
        handleBlockUser(menuResidentId);
        break;
      case "unblock":
        handleUnblockUser(menuResidentId);
        break;
    }
    handleCloseMenu();
  };

  // Handle next page
  const handleNextPage = () => {
    const next = residentsData?.pageToken?.trim();
    if (next) {
      setPageToken(next);
    }
  };

  // Handle previous page
  const handlePreviousPage = () => {
    setPageToken(undefined);
  };

  if (isLoadingResidents) {
    return <LoadingIndicator />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography fontWeight={600} fontSize={24}>
          Управление резидентами
        </Typography>

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportToXlsx}
          disabled={isExporting}
          sx={{ ml: 2 }}
        >
          {isExporting ? "Экспорт..." : "Экспорт в Excel"}
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ mb: 1, p: 3, pb: 1, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <ArrowUpwardIcon fontSize="small" />
            <ArrowDownwardIcon fontSize="small" />
            Используйте выпадающий список "Сортировка" для изменения порядка
            отображения резидентов
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid sx={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Поиск по имени или фамилии"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              sx={{ width: 200 }}
            >
              <InputLabel>Города</InputLabel>
              <Select
                multiple
                value={selectedCities}
                onChange={(e) => setSelectedCities(e.target.value as number[])}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((cityId) => {
                      const city = citiesData?.cities.find(
                        (c) => c.id === cityId,
                      );
                      return city ? (
                        <Chip key={cityId} label={city.name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {citiesData?.cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl size="small" variant="outlined" sx={{ width: 200 }}>
              <InputLabel>Уровень резидента</InputLabel>
              <Select
                multiple
                value={selectedCalibers}
                onChange={(e) =>
                  setSelectedCalibers(e.target.value as ResidentCaliber[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((caliber) => (
                      <Chip
                        key={caliber}
                        label={renderCaliberName(caliber)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="voshod">Восход</MenuItem>
                <MenuItem value="siyay">Сияй</MenuItem>
                <MenuItem value="sover">Совер</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl size="small" variant="outlined" sx={{ width: 200 }}>
              <InputLabel>Статус</InputLabel>
              <Select
                value={
                  blockedFilter === undefined ? "" : blockedFilter.toString()
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setBlockedFilter(value === "" ? undefined : value === "true");
                }}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="false">Не заблокированные</MenuItem>
                <MenuItem value="true">Заблокированные</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Сортировка</InputLabel>
              <Select
                value={sort}
                onChange={(e) =>
                  setSort(
                    e.target.value as
                      | "firstname"
                      | "lastname"
                      | "inviteat"
                      | "createdat",
                  )
                }
              >
                <MenuItem value="firstname">По имени</MenuItem>
                <MenuItem value="lastname">По фамилии</MenuItem>
                <MenuItem value="createdat">По дате регистрации</MenuItem>
                <MenuItem value="inviteat">По дате приглашения</MenuItem>
              </Select>
            </FormControl>
            <Grid>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
                startIcon={
                  order === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                }
                sx={{
                  minWidth: 160,
                  borderColor: "primary.main",
                  "&:hover": {
                    borderColor: "primary.dark",
                    backgroundColor: "primary.light",
                  },
                }}
              >
                {order === "asc" ? "По возрастанию" : "По убыванию"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Residents Table */}
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{ borderRadius: 2, mb: 3 }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "secondary.main" }}>
            <TableRow>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  Фото
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  Имя
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneIcon fontSize="small" />
                  Телефон
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <StarIcon fontSize="small" />
                  Уровень
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EventIcon fontSize="small" />
                  Статус
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayIcon fontSize="small" />
                  Дата регистрации
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EventIcon fontSize="small" />
                  Дата приглашения
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                  Действия
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isSorting ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Обновление сортировки...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              residentsData?.residents.map((resident) => (
                <TableRow
                  key={resident.id}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Avatar
                      src={resident.avatarUrl}
                      alt={resident.firstName}
                      sx={{ width: 45, height: 45 }}
                    >
                      {resident.firstName.charAt(0)}
                      {resident.lastName.charAt(0)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    {resident.firstName + " " + resident.lastName}
                  </TableCell>
                  <TableCell>{resident.phoneNumber}</TableCell>
                  <TableCell>
                    <Chip
                      label={renderCaliberName(resident.caliber)}
                      color={
                        resident.caliber === "voshod"
                          ? "default"
                          : resident.caliber === "siyay"
                            ? "primary"
                            : "secondary"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={resident.blocked ? "Заблокирован" : "Активен"}
                      color={resident.blocked ? "error" : "success"}
                      icon={resident.blocked ? <BlockIcon /> : <CheckCircleIcon />}
                      sx={{
                        "& .MuiChip-icon": {
                          color: "#fff",
                          fontSize: "16px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {resident.createdAt
                      ? format(new Date(resident.createdAt), "dd.MM.yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {resident.invitedAt
                      ? format(new Date(resident.invitedAt), "dd.MM.yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenDetails(resident.id)}
                        sx={{ minWidth: "auto", p: 1 }}
                      >
                        <PersonIcon fontSize="small" sx={{ color: "#fff" }} />
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        sx={{ minWidth: "auto", p: 1 }}
                        onClick={(e) => handleOpenMenu(e, resident.id)}
                      >
                        <EditIcon fontSize="small" sx={{ color: "#fff" }} />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <MenuItem onClick={() => handleMenuAction("caliber")}>
          <ListItemIcon>
            <ExtensionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Изменить уровень" />
        </MenuItem>
        {menuResidentId && (
          <MenuItem
            onClick={() =>
              handleMenuAction(
                residentsData?.residents.find((r) => r.id === menuResidentId)
                  ?.blocked
                  ? "unblock"
                  : "block",
              )
            }
          >
            <ListItemIcon>
              {residentsData?.residents.find((r) => r.id === menuResidentId)
                ?.blocked ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <BlockIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                residentsData?.residents.find((r) => r.id === menuResidentId)
                  ?.blocked
                  ? "Разблокировать"
                  : "Заблокировать"
              }
            />
          </MenuItem>
        )}
      </Menu>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={3}>
        <Button
          disabled={!pageToken}
          onClick={handlePreviousPage}
          variant="contained"
          color="secondary"
          sx={{ mr: 2 }}
          startIcon={<NavigateBeforeIcon />}
        >
          Предыдущая
        </Button>
        <Button
          disabled={!residentsData?.pageToken?.trim()}
          onClick={handleNextPage}
          variant="contained"
          color="secondary"
          endIcon={<NavigateNextIcon />}
        >
          Следующая
        </Button>
      </Box>

      {/* Resident Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 0,
          }}
        >
          <Tabs
            value={detailTabValue}
            onChange={(_, newValue) => setDetailTabValue(newValue)}
            variant="fullWidth"
          >
            <Tab label="Информация" />
            <Tab label="Периоды резиденства" />
          </Tabs>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {detailTabValue === 0 && (
            <ResidentDetailView
              isFetching={isFetchingResidentDetail}
              resident={residentDetail?.profile ?? null}
            />
          )}
          {detailTabValue === 1 && (
            <ResidentPeriodsView
              isFetching={isFetchingPeriods}
              periods={residentPeriods?.periods}
              emptyHint="У данного резидента еще нет записей о периодах резиденства"
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{ borderTop: "1px solid", borderColor: "divider", py: 2 }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            color="secondary"
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Caliber Update Dialog */}
      <Dialog
        open={openCaliberDialog}
        onClose={handleCloseCaliberDialog}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 2,
            textTransform: "none",
          }}
        >
          <Typography>Изменить уровень резидента</Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3, minWidth: 300 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Уровень резидента</InputLabel>
            <Select
              value={selectedCaliber}
              onChange={(e) =>
                setSelectedCaliber(e.target.value as ResidentCaliber)
              }
            >
              <MenuItem value="voshod">Восход</MenuItem>
              <MenuItem value="siyay">Сияй</MenuItem>
              <MenuItem value="sover">Совер</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={handleCloseCaliberDialog} color="inherit">
            Отмена
          </Button>
          <Button
            onClick={handleUpdateCaliber}
            variant="contained"
            color="primary"
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Component for displaying resident details
const ResidentDetailView = ({
  isFetching,
  resident,
}: {
  isFetching: boolean;
  resident: ResidentDetailed | null;
}) => {
  if (isFetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!resident) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
          color: "text.secondary",
        }}
      >
        <Typography>Нет данных о резиденте</Typography>
      </Box>
    );
  }
  return (
    <Grid container columnSpacing={2} rowSpacing={0}>
      <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Avatar
            src={resident.avatarUrl}
            alt={resident.firstName}
            sx={{
              width: 180,
              height: 180,
              mb: 2,
              border: "3px solid",
              borderColor: "primary.main",
            }}
          >
            {resident.firstName.charAt(0)}
            {resident.lastName.charAt(0)}
          </Avatar>
          <Typography variant="h5" gutterBottom>
            {resident.firstName} {resident.lastName}
          </Typography>
          {resident.additionalStatus && (
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              {resident.additionalStatus}
            </Typography>
          )}
          <Chip
            label={
              resident.caliber === "voshod"
                ? "Восходящая звезда"
                : resident.caliber === "siyay"
                  ? "Сияющая звезда"
                  : "Совершенная звезда"
            }
            color={
              resident.caliber === "voshod"
                ? "default"
                : resident.caliber === "siyay"
                  ? "primary"
                  : "secondary"
            }
            sx={{ mt: 1, mb: 2 }}
          />
          <Chip
            label={resident.blocked ? "Заблокирован" : "Активен"}
            color={resident.blocked ? "error" : "success"}
            sx={{ mt: 1, mb: 2, ml: 1 }}
          />
          <Paper
            elevation={1}
            sx={{ p: 3, width: "100%", maxWidth: "100%", borderRadius: 2, overflow: "hidden" }}
          >
            <Typography
              variant="body1"
              sx={{ display: "flex", alignItems: "flex-start", mb: 1, minWidth: 0 }}
            >
              <PhoneIcon fontSize="small" sx={{ mr: 1, mt: 0.25, flexShrink: 0 }} />
              {resident.phoneNumber}
            </Typography>
            {resident.email && (
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1,
                  minWidth: 0,
                }}
              >
                <EmailIcon fontSize="small" sx={{ mr: 1, mt: 0.25, flexShrink: 0 }} />
                <Box
                  component="span"
                  sx={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {resident.email}
                </Box>
              </Typography>
            )}
            {resident.birthDate && (
              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}
              >
                <CalendarTodayIcon fontSize="small" sx={{ mr: 1, mt: 0.25, flexShrink: 0 }} />
                Дата рождения:{" "}
                {format(new Date(resident.birthDate), "dd.MM.yyyy")}
              </Typography>
            )}
          </Paper>
        </Box>
      </Grid>
      <Grid
        size={{ xs: 12, md: 8 }}
        sx={{ display: "flex", flexDirection: "column", gap: "5px" }}
      >
        {resident.about && (
          <Box sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PersonIcon sx={{ mr: 1 }} />О резиденте
            </Typography>
            <Typography variant="body1">{resident.about}</Typography>
          </Box>
        )}
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <WorkIcon sx={{ mr: 1 }} />
            Компетенции
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {resident.competencies.length ? (
              resident.competencies.map((comp) => (
                <Chip
                  key={comp.id}
                  label={comp.name}
                  color="primary"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body1">
                У резидента нет компетенций
              </Typography>
            )}
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <BusinessIcon sx={{ mr: 1 }} />
            Компании
          </Typography>
          {resident.companies.length ? (
            resident.companies.map((company) => (
              <Paper
                key={company.id}
                sx={{
                  p: 2,
                  mb: "5px",
                  "&:last-of-type": { mb: 0 },
                  bgcolor: "secondary.light",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  {company.name}
                </Typography>
                {company.description && (
                  <Typography variant="body2" sx={{ my: 1 }}>
                    {company.description}
                  </Typography>
                )}
                {company.employeeRole && (
                  <Typography
                    variant="body2"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <BadgeIcon fontSize="small" sx={{ mr: 1 }} />
                    Роль: {company.employeeRole}
                  </Typography>
                )}
              </Paper>
            ))
          ) : (
            <Typography variant="body1">У резидента нет компаний</Typography>
          )}
        </Paper>

        {resident.hobbies && resident.hobbies.length > 0 && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <SportsEsportsIcon sx={{ mr: 1 }} />
              Хобби
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {resident.hobbies.map((hobby) => (
                <Chip key={hobby.id} label={hobby.name} variant="outlined" />
              ))}
            </Box>
          </Paper>
        )}

        {resident.socials && resident.socials.length > 0 && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <LinkIcon sx={{ mr: 1 }} />
              Социальные сети
            </Typography>
            <List dense>
              {resident.socials.map((social, index) => (
                <ListItem key={index} sx={{ px: 1 }}>
                  <ListItemIcon>
                    {social.type === "facebook" && <FacebookIcon />}
                    {social.type === "instagram" && <InstagramIcon />}
                    {social.type === "linkedin" && <LinkedInIcon />}
                    {social.type === "vk" && (
                      <img src="/vk-icon.svg" width="24" />
                    )}
                    {social.type === "telegram" && <TelegramIcon />}
                    {![
                      "facebook",
                      "instagram",
                      "linkedin",
                      "vk",
                      "telegram",
                    ].includes(social.type) && <LanguageIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      social.type.charAt(0).toUpperCase() + social.type.slice(1)
                    }
                    secondary={
                      <Link
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary.main"
                      >
                        {social.url}
                      </Link>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {resident.activity && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <BarChartIcon sx={{ mr: 1 }} />
              Активность
            </Typography>
            <Grid container spacing={2}>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "primary.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.participantCount}
                  </Typography>
                  <Typography variant="body2">Участие в событиях</Typography>
                </Paper>
              </Grid>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "secondary.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.requestCount}
                  </Typography>
                  <Typography variant="body2">Заявки</Typography>
                </Paper>
              </Grid>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "info.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.commentCount}
                  </Typography>
                  <Typography variant="body2">Комментарии</Typography>
                </Paper>
              </Grid>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "success.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.invitationCount}
                  </Typography>
                  <Typography variant="body2">Приглашения</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default ResidentsManagement;
